import type { EstimatorToolInput } from "./chat-tools";
import type { EstimatorToolOutput, PricedWindow, RankedCity } from "./chat-messages";
import { MAX_DESTINATIONS, MAX_WINDOW_DESTINATIONS } from "./constants";
import { fromUsd } from "./estimate";
import { placeholderDates, rankCities, withHotelRates } from "./explore";

import { FARE_GAP, describeFareGaps, faresPublished, type SearchEntry } from "./flight-search";
import { patchStep, type LiveStep } from "./live-steps";
import type { Estimate, FareQuote, HotelRate, Workspace, WorkspaceAction } from "./types";
import { cityKey } from "./workspace";
import type { EstimatorMoney } from "./types";

/** What the explore tools read and write. Reads go through `snapshot`, so they see the latest state. */
export interface ExploreDeps {
  snapshot: () => {
    workspace: Workspace;
    estimate: Estimate;
    rates: Readonly<Record<string, number>>;
    /** The page's money formatting, so progress reads like the table. */
    money: EstimatorMoney;
    fares: ReadonlyMap<string, FareQuote>;
    hotels: ReadonlyMap<string, HotelRate>;
  };
  /** Runs the flight search for another version of the workspace; `onUpdate` sees results as they land. */
  run: (
    workspace: Workspace,
    onUpdate?: (entry: SearchEntry, quotes: Map<string, FareQuote>) => void
  ) => Promise<{ quotes: Map<string, FareQuote> } | null>;
  /** Replaces the tool call's live work log. */
  report: (update: (steps: LiveStep[]) => LiveStep[]) => void;
  edit: (action: WorkspaceAction) => { ok: true; undo: WorkspaceAction } | { ok: false; error: string };
}

/** An explore tool's answer, plus the undo for any cities it added. */
export interface ExploreResult {
  output: EstimatorToolOutput;
  change: { label: string; undo: WorkspaceAction[] } | null;
}

const display = (usd: number | null, currency: string, rates: Readonly<Record<string, number>>) =>
  usd === null ? null : Math.round(fromUsd(usd, currency, rates) ?? usd);

/** Airports when the search has resolved them, else the city names. */
const placesLabel = (cities: string[], airports: Record<string, string | null> | undefined) =>
  cities.map((city) => airports?.[cityKey(city)] ?? city).join(", ");

/** Why a set of routes has no fare, as the work log and the tool output both say it. */
const fareGapReason = (quotes: Iterable<FareQuote>): string =>
  describeFareGaps([...quotes].flatMap((quote) => (quote.status === "missing" ? [quote.reason] : []))) ??
  "No fares found";

/**
 * One search's progress in words: routes priced so far, flights found, and the cheapest fare
 * with its airline, e.g. "14 flights · cheapest TAP Air Portugal $182". With nothing priced,
 * the reason: "Too many searches, try again in a few minutes", "No airport found for Napa".
 */
const searchDetail = (entry: SearchEntry, quotes: Map<string, FareQuote>, money: EstimatorMoney): string => {
  const all = [...quotes.values()];
  const priced = all.filter((quote): quote is Extract<FareQuote, { status: "priced" }> => quote.status === "priced");
  const settled = all.filter((quote) => quote.status !== "loading").length;
  if (entry.error) return entry.error;
  if (priced.length === 0) {
    if (!settled) return `Searching${entry.poll ? ` · ${Math.round(entry.poll.progress * 100)}%` : ""}`;
    return fareGapReason(all);
  }
  const flights = priced.reduce((sum, quote) => sum + 1 + (quote.flight?.otherOptions ?? 0), 0);
  const cheapest = priced.reduce((best, quote) => (quote.usd < best.usd ? quote : best));
  const airline = cheapest.flight?.airline ? `${cheapest.flight.airline} ` : "";
  return `${flights} ${flights === 1 ? "flight" : "flights"} · cheapest ${airline}${money.format(cheapest.usd)}`;
};

/**
 * Prices candidate date windows for the current home cities and destinations, so the planner
 * can pick dates by price. Fares land in the shared cache, so picking a window is instant.
 * @param input - The windows the model proposed.
 * @param deps - State access, cache priming and edits.
 * @returns Each window's cheapest city and per-person range, in the display currency, or why it has no fare.
 * @throws Never; failed lookups count as missing fares.
 */
export async function compareDateWindows(
  input: EstimatorToolInput<"compare_date_windows">,
  deps: ExploreDeps
): Promise<ExploreResult> {
  const { workspace, hotels, rates } = deps.snapshot();
  const cities = workspace.destinations.slice(0, MAX_WINDOW_DESTINATIONS);
  if (workspace.origins.length === 0 || cities.length === 0) {
    return {
      output: { ok: false, error: "Needs home cities and at least one destination before dates can be priced." },
      change: null,
    };
  }

  const origins = workspace.origins.map((origin) => origin.city);
  deps.report(() =>
    patchStep(
      [],
      "search",
      {},
      `Searching ${origins.join(", ")} → ${cities.join(", ")} for ${input.windows.length} date windows`
    )
  );
  const hotelsPromise = (async () => {
    deps.report((steps) => patchStep(steps, "hotels", {}, `Checking hotel rates in ${cities.join(", ")}`));
    const map = await withHotelRates(cities, hotels);
    const venues = cities.reduce((sum, city) => {
      const rate = map.get(cityKey(city));
      return sum + (rate?.status === "venues" ? rate.venueCount : 0);
    }, 0);
    deps.report((steps) =>
      patchStep(steps, "hotels", {
        status: "done",
        detail: venues ? `${venues} venues` : "No rate data, using your assumption",
      })
    );
    return map;
  })();
  const searched = await Promise.all(
    input.windows.map((window) => {
      // Past the airlines' publish window there is nothing to search: say so instead of "no fares".
      if (!faresPublished(window)) {
        deps.report((steps) =>
          patchStep(steps, `window:${window.start}`, { detail: FARE_GAP.unpublished, status: "done" }, window.label)
        );
        return null;
      }
      return deps.run({ ...workspace, destinations: cities, dates: window }, (entry, quotes) => {
        if (entry.started?.airports) {
          deps.report((steps) =>
            patchStep(steps, "search", {
              label: `Searching ${placesLabel(origins, entry.started?.airports)} → ${placesLabel(cities, entry.started?.airports)} for ${input.windows.length} date windows`,
            })
          );
        }
        const settled = entry.error || entry.poll?.status === "complete" || entry.poll?.status === "error";
        deps.report((steps) =>
          patchStep(
            steps,
            `window:${window.start}`,
            { detail: searchDetail(entry, quotes, deps.snapshot().money), status: settled ? "done" : "running" },
            window.label
          )
        );
      });
    })
  );
  deps.report((steps) => patchStep(steps, "search", { status: "done" }));
  const hotelMap = await hotelsPromise;
  const windows: PricedWindow[] = input.windows.map((window, index) => {
    const quotes = searched[index]?.quotes ?? new Map<string, FareQuote>();
    const [best] = rankCities(workspace, cities, window, quotes, hotelMap);
    const priced = best && best.flightUsd !== null;
    const unpublished = !faresPublished(window);
    return {
      ...window,
      cheapestCity: priced ? best.city : null,
      perPersonLow: priced ? display(best.perPerson.low, workspace.currency, rates) : null,
      perPersonHigh: priced ? display(best.perPerson.high, workspace.currency, rates) : null,
      fare: best?.cheapestFare ? display(best.cheapestFare.usd, workspace.currency, rates) : null,
      airline: best?.cheapestFare?.airline ?? null,
      fareGap: priced ? null : unpublished ? FARE_GAP.unpublished : fareGapReason(quotes.values()),
    };
  });
  return {
    output: {
      ok: true,
      applied: `Priced ${windows.length} date windows`,
      windows,
    },
    change: null,
  };
}

/**
 * Prices candidate destinations for the current home cities, adds the cheapest ones to the
 * comparison (undoable as one change), and reports the full ranking.
 * @param input - Candidate cities with reasons, and how many to add.
 * @param deps - State access, cache priming and edits.
 * @returns The ranking in the display currency, and the change that added cities.
 * @throws Never; failed lookups count as missing fares.
 */
export async function suggestDestinations(
  input: EstimatorToolInput<"suggest_destinations">,
  deps: ExploreDeps
): Promise<ExploreResult> {
  const { workspace, estimate, hotels, rates } = deps.snapshot();
  if (workspace.origins.length === 0) {
    return { output: { ok: false, error: "Needs home cities first: who is coming, and from where?" }, change: null };
  }

  const existing = new Set(workspace.destinations.map(cityKey));
  const candidates = input.candidates.filter((candidate) => !existing.has(cityKey(candidate.city)));
  const names = candidates.map((candidate) => candidate.city);
  const dates = workspace.dates ?? placeholderDates(Math.max(estimate.nights, 2));

  const origins = workspace.origins.map((origin) => origin.city);
  deps.report(() => patchStep([], "search", {}, `Pricing ${names.length} cities from ${origins.join(", ")}`));
  const [searched, hotelMap] = await Promise.all([
    // Past the airlines' publish window there is nothing to search: each city says so.
    faresPublished(dates)
      ? deps.run({ ...workspace, destinations: names, dates }, (entry, quotes) => {
          for (const name of names) {
            const cityQuotes = new Map([...quotes].filter(([key]) => key.endsWith(`|${cityKey(name)}`)));
            const settled =
              entry.error ||
              entry.poll?.status === "complete" ||
              [...cityQuotes.values()].every((quote) => quote.status !== "loading");
            deps.report((steps) =>
              patchStep(
                steps,
                `city:${cityKey(name)}`,
                {
                  detail: searchDetail(entry, cityQuotes, deps.snapshot().money),
                  status: settled ? "done" : "running",
                },
                name
              )
            );
          }
        })
      : (() => {
          deps.report((steps) =>
            names.reduce(
              (all, name) =>
                patchStep(all, `city:${cityKey(name)}`, { detail: FARE_GAP.unpublished, status: "done" }, name),
              steps
            )
          );
          return null;
        })(),
    (async () => {
      deps.report((steps) => patchStep(steps, "hotels", {}, `Checking hotel rates in ${names.length} cities`));
      const map = await withHotelRates(names, hotels);
      const venues = names.reduce((sum, city) => {
        const rate = map.get(cityKey(city));
        return sum + (rate?.status === "venues" ? rate.venueCount : 0);
      }, 0);
      deps.report((steps) => patchStep(steps, "hotels", { status: "done", detail: `${venues} venues` }));
      return map;
    })(),
  ]);
  deps.report((steps) => patchStep(steps, "search", { status: "done" }));
  const ranked = rankCities(workspace, names, dates, searched?.quotes ?? new Map(), hotelMap);

  const room = Math.max(0, MAX_DESTINATIONS - workspace.destinations.length);
  const toAdd = ranked.filter((city) => city.flightUsd !== null).slice(0, Math.min(input.add, room));
  const undo: WorkspaceAction[] = [];
  for (const city of toAdd) {
    const result = deps.edit({ type: "addDestination", city: city.city });
    if (result.ok) undo.unshift(result.undo);
  }
  const added = new Set(toAdd.map((city) => cityKey(city.city)));

  const reasonFor = new Map(candidates.map((candidate) => [cityKey(candidate.city), candidate.reason]));
  const rows: RankedCity[] = ranked.map((city) => ({
    city: city.city,
    reason: reasonFor.get(cityKey(city.city)) ?? "",
    perPersonLow: city.flightUsd === null ? null : display(city.perPerson.low, workspace.currency, rates),
    perPersonHigh: city.flightUsd === null ? null : display(city.perPerson.high, workspace.currency, rates),
    flight: display(city.flightUsd, workspace.currency, rates),
    added: added.has(cityKey(city.city)),
  }));

  return {
    output: {
      ok: true,
      applied: toAdd.length ? `Added ${toAdd.map((city) => city.city).join(", ")}` : "Added no cities",
      ranked: rows,
      assumedDates: workspace.dates ? undefined : dates,
    },
    change: toAdd.length ? { label: `Added ${toAdd.map((city) => city.city).join(", ")}`, undo } : null,
  };
}
